import { useMainStore } from '~/store'
import { computed } from 'vue'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezonePlugin from 'dayjs/plugin/timezone'
import isoWeek from 'dayjs/plugin/isoWeek'

dayjs.extend(utc)
dayjs.extend(timezonePlugin)
dayjs.extend(isoWeek)

export const usePropertyHours = async (storeData) => {
  const store = useMainStore()
  const timezone = store.property?.time_zone || 'America/New_York' // default fallback

  const { data: mallHoursRaw } = await $fetch(`${store.baseURL}/hours`)
  const propertyRegularHours = store.property?.hours?.regular_hours || []
  const propertyHolidayHours = store.property?.hours?.holiday_hours || []

  const storeRegularHours = storeData?.hours?.regular_hours || []
  const storeHolidayHours = storeData?.hours?.holiday_hours || []

  const today = dayjs().tz(timezone)
  const currentWeek = today.isoWeek()

  function normalizeDayOfWeek(day) {
    return day === 0 ? 7 : day // Convert Sunday from 0 to 7
  }

  function expandHolidayHours(holidayHoursList) {
    const expanded = []

    holidayHoursList.forEach(holiday => {
      const start = dayjs(holiday.holiday_date).tz(timezone).startOf('day')
      const end = holiday.holiday_end_date ? dayjs(holiday.holiday_end_date).tz(timezone).endOf('day') : start.endOf('day')

      const duration = end.diff(start, 'days') + 1

      for (let i = 0; i < duration; i++) {
        const current = start.add(i, 'day')
      
        if (current.isoWeek() === currentWeek) {
          const isClosedDay = holiday.is_closed
          
          expanded.push({
            day_of_week: normalizeDayOfWeek(current.day()),
            open_time: holiday.open_time,
            close_time: holiday.close_time,
            is_closed: isClosedDay,
            is_holiday: true,
            holiday_name: holiday.holiday_name,
          })
        }
      }
      
    })

    return expanded
  }

  // New helper to format time, showing minutes only if not zero
  function formatTime(timeString) {
    const dt = dayjs(timeString).tz(timezone)
    return dt.minute() === 0
      ? dt.format('h A')      // e.g., 3 PM
      : dt.format('h:mm A')   // e.g., 3:15 PM
  }

  const baseHours = computed(() => {
    if (storeRegularHours.length > 0) return storeRegularHours
    return propertyRegularHours
  })

  const combinedHolidayHours = computed(() => {
    const storeHolidays = expandHolidayHours(storeHolidayHours)
    const propertyHolidays = expandHolidayHours(propertyHolidayHours)
    return [...storeHolidays, ...propertyHolidays]
  })

  const finalHours = computed(() => {
    const hours = []

    for (let i = 1; i <= 7; i++) {
      const holidaysForThisDay = combinedHolidayHours.value.filter(h => h.day_of_week === i)

      const holiday = holidaysForThisDay.length > 0 ? holidaysForThisDay[0] : null

      if (holiday) {
        hours.push({
          day: dayjs().isoWeekday(i).format('dddd'),
          hours: holiday.is_closed
            ? 'Closed'
            : `${formatTime(holiday.open_time)} - ${formatTime(holiday.close_time)}`,
          isHoliday: true,
          holidayName: holiday.holiday_name || null,
        })
      } else {
        const match = baseHours.value.find(h => normalizeDayOfWeek(h.day_of_week) === i)

        hours.push({
          day: dayjs().isoWeekday(i).format('dddd'),
          hours: match
            ? match.is_closed
              ? 'Closed'
              : `${formatTime(match.open_time)} - ${formatTime(match.close_time)}`
            : 'Closed',
          isHoliday: false,
          holidayName: null,
        })
      }
    }

    return hours
  })

  return {
    finalHours,
    todaysHours: storeData?.hours?.todays_hours || null,
  }
}

